import { format } from 'date-fns';
import { doc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, CategoryPicker, DatePicker, Input, LoadingSpinner, MapboxGeocoder } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { cityService, type City } from '../services/cityService';
import type { Event, LocationData } from '../services/eventService';
import { db, storage } from '../utils/firebaseConfig';

export function EditEventPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const errorRef = React.useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dateRange: { from: undefined as Date | undefined, to: undefined as Date | undefined },
    startTime: '',
    endTime: '',
    category: '',
    isFree: true,
    cost: 0,
    tags: [] as string[],
    image: null as File | null,
    website: ''
  });

  const [location, setLocation] = useState<LocationData>();
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [linkPreview, setLinkPreview] = useState<{
    title?: string;
    description?: string;
    favicon?: string;
    image?: string;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [timeError, setTimeError] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string>('');
  const [originalEvent, setOriginalEvent] = useState<Event | null>(null);

  // Fetch event data
  useEffect(() => {
    const loadEvent = async () => {
      if (!id) {
        setError('Event ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const eventDoc = await getDoc(doc(db, 'events', id));

        if (!eventDoc.exists()) {
          setError('Event not found');
          setLoading(false);
          return;
        }

        const eventData = { id: eventDoc.id, ...eventDoc.data() } as Event;
        setOriginalEvent(eventData);

        // Check if user is owner
        if (user?.uid !== eventData.createdBy) {
          setError('You do not have permission to edit this event');
          setLoading(false);
          return;
        }

        // Populate form with existing data
        setFormData({
          title: eventData.title,
          description: eventData.description,
          dateRange: {
            from: eventData.startDate ? new Date(eventData.startDate) : undefined,
            to: eventData.endDate ? new Date(eventData.endDate) : undefined,
          },
          startTime: eventData.startTime || '',
          endTime: eventData.endTime || '',
          category: eventData.category,
          isFree: eventData.isFree,
          cost: eventData.cost || 0,
          tags: eventData.tags || [],
          image: null,
          website: eventData.website || ''
        });

        if (eventData.location) {
          setLocation(eventData.location);
        }

        if (eventData.city) {
          setSelectedCity(eventData.city);
        }

        if (eventData.image) {
          setExistingImageUrl(eventData.image);
          setImagePreview(eventData.image);
        }

        if (eventData.linkPreview) {
          setLinkPreview(eventData.linkPreview);
        }

        // Clear any errors since we successfully loaded the event
        setError('');
        setLoading(false);
      } catch (err) {
        console.error('Error loading event:', err);
        setError('Failed to load event');
        setLoading(false);
      }
    };

    loadEvent();
  }, [id, user]);

  // Fetch cities on mount
  useEffect(() => {
    const loadCities = async () => {
      try {
        const fetchedCities = await cityService.fetchCities();
        setCities(fetchedCities);
      } catch (error) {
        console.error('Error loading cities:', error);
        setError('Failed to load cities. Please refresh the page.');
      }
    };
    loadCities();
  }, []);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleLocationSelect = async (location: LocationData) => {
    let matchedCity = await cityService.findCityByCoordinates(
      location.coordinates.latitude,
      location.coordinates.longitude
    );

    if (!matchedCity) {
      matchedCity = await cityService.findCityByLocation(location.city);
    }

    if (!matchedCity) {
      const cityNames = cities.map(c => c.name).join(' or ');
      setError(`Events can only be added in ${cityNames}`);
      setLocation(undefined);
      setSelectedCity(null);
      return;
    }

    setError('');
    setLocation(location);
    setSelectedCity(matchedCity);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const normalizeUrl = (url: string): string => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return '';
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }
    return `https://${trimmedUrl}`;
  };

  const isValidUrl = (url: string) => {
    if (!url) return false;
    const normalized = normalizeUrl(url);
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const fetchLinkPreview = async (url: string) => {
    if (!url || !isValidUrl(url)) {
      setLinkPreview(null);
      return;
    }

    const normalizedUrl = normalizeUrl(url);
    setPreviewLoading(true);
    try {
      const apiUrl = `https://api.linkpreview.net?q=${encodeURIComponent(normalizedUrl)}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'X-Linkpreview-Api-Key': 'aff3406ea2a89aae35c97731d13910fc'
        }
      });
      const data = await response.json();

      if (data && !data.error) {
        setLinkPreview({
          title: data.title,
          description: data.description,
          favicon: data.image,
          image: data.image
        });
      } else {
        const urlObj = new URL(normalizedUrl);
        setLinkPreview({
          title: urlObj.hostname,
          description: normalizedUrl,
          favicon: `${urlObj.protocol}//${urlObj.host}/favicon.ico`
        });
      }
    } catch (error) {
      console.error('Error fetching link preview:', error);
      try {
        const urlObj = new URL(normalizedUrl);
        setLinkPreview({
          title: urlObj.hostname,
          description: normalizedUrl,
          favicon: `${urlObj.protocol}//${urlObj.host}/favicon.ico`
        });
      } catch {
        setLinkPreview(null);
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.website) {
        fetchLinkPreview(formData.website);
      } else {
        setLinkPreview(null);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.website]);

  useEffect(() => {
    if (formData.startTime && formData.endTime && formData.dateRange.from) {
      const isSingleDay = !formData.dateRange.to ||
        formData.dateRange.from?.toDateString() === formData.dateRange.to?.toDateString();

      if (isSingleDay && formData.endTime <= formData.startTime) {
        setTimeError('End time must be after start time for single-day events');
      } else {
        setTimeError('');
      }
    } else {
      setTimeError('');
    }
  }, [formData.startTime, formData.endTime, formData.dateRange]);

  const uploadImage = async (file: File): Promise<string> => {
    const filename = `events/${id}/${Date.now()}_${file.name}`;
    const imageRef = ref(storage, filename);
    const snapshot = await uploadBytes(imageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id) return;

    setError('');
    setSubmitting(true);

    try {
      if (!formData.title.trim()) {
        throw new Error('Event title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Event description is required');
      }
      if (!formData.dateRange.from) {
        throw new Error('Event start date is required');
      }
      if (!formData.startTime) {
        throw new Error('Event start time is required');
      }
      if (!formData.endTime) {
        throw new Error('Event end time is required');
      }

      const isSingleDay = !formData.dateRange.to ||
        formData.dateRange.from?.toDateString() === formData.dateRange.to?.toDateString();

      if (isSingleDay) {
        if (formData.endTime <= formData.startTime) {
          throw new Error('End time must be after start time for single-day events');
        }
      }

      if (!formData.category) {
        throw new Error('Event category is required');
      }
      if (!location) {
        throw new Error('Event location is required');
      }
      if (!selectedCity) {
        throw new Error('Please select a valid location in one of our supported cities');
      }

      let imageUrl = existingImageUrl;
      if (formData.image) {
        imageUrl = await uploadImage(formData.image);
      } else if (!imageUrl && linkPreview?.image) {
        imageUrl = linkPreview.image;
      }

      const formatDateToISO = (date: Date): string => {
        return format(date, 'yyyy-MM-dd');
      };

      const updateData: Partial<Event> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        startDate: formatDateToISO(formData.dateRange.from!),
        endDate: formData.dateRange.to ? formatDateToISO(formData.dateRange.to) : formatDateToISO(formData.dateRange.from!),
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: location,
        city: selectedCity,
        category: formData.category,
        isFree: formData.isFree,
        cost: formData.isFree ? 0 : formData.cost,
        tags: formData.tags,
        image: imageUrl,
        website: formData.website ? normalizeUrl(formData.website) : null,
        linkPreview: linkPreview || null,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(doc(db, 'events', id), updateData);
      navigate(`/events/${id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to update event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 mt-4">Loading event...</p>
      </div>
    );
  }

  if (error && !originalEvent) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{error}</h2>
        <Button onClick={() => navigate('/')}>
          Back to Events
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0 pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Edit Event</h1>
        <p className="text-gray-600 mt-2">Update your event details</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Event Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Event Title *
            </label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter event title"
              required
              className="w-full"
            />
          </div>

          {/* Event Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your event..."
              required
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>

          {/* Date Range and Category Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Event Date Range *
              </label>
              <DatePicker
                value={formData.dateRange}
                onChange={(dateRange) => setFormData(prev => ({
                  ...prev,
                  dateRange: {
                    from: dateRange.from,
                    to: dateRange.to
                  }
                }))}
                placeholder="Select event date range"
                className="w-full"
                minDate={new Date()}
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <CategoryPicker
                value={formData.category}
                onChange={(category) => setFormData(prev => ({ ...prev, category }))}
                placeholder="Select category"
                className="w-full"
              />
            </div>
          </div>

          {/* Time Row */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <Input
                  id="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  placeholder="Select start time"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-2">
                  End Time *
                </label>
                <Input
                  id="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  placeholder="Select end time"
                  required
                  className="w-full"
                />
              </div>
            </div>
            {timeError && (
              <p className="mt-2 text-sm text-red-600">{timeError}</p>
            )}
          </div>

          {/* Location with Mapbox */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
              Location *
            </label>
            {cities.length > 0 && (
              <p className="text-sm text-gray-500 mb-2">
                Events must be located in {cities.map(c => c.name).join(' or ')}
              </p>
            )}
            <MapboxGeocoder
              placeholder="Search for event location..."
              onLocationSelect={handleLocationSelect}
              className="w-full"
            />
            {location && selectedCity && (
              <p className="text-sm text-green-600 mt-2">
                ✓ {location.name} ({selectedCity.name})
              </p>
            )}
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Event Pricing
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="pricing"
                  checked={formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: true, cost: 0 }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Free Event</span>
              </label>

              <label className="flex items-center">
                <input
                  type="radio"
                  name="pricing"
                  checked={!formData.isFree}
                  onChange={() => setFormData(prev => ({ ...prev, isFree: false }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Paid Event</span>
              </label>

              {!formData.isFree && (
                <div className="ml-6 mt-2">
                  <Input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: Number(e.target.value) }))}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-32"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              Website (optional)
            </label>
            <Input
              id="website"
              type="text"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              placeholder="example.com or https://example.com"
              className="w-full"
            />
            {(linkPreview || previewLoading) && (
              <div className="mt-3">
                {previewLoading ? (
                  <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-600">Loading preview...</span>
                  </div>
                ) : linkPreview && (
                  <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                    <div className="p-4 flex gap-4 items-center">
                      {linkPreview.image && (
                        <div className="w-32 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={linkPreview.image}
                            alt="Website preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-sm font-medium text-gray-900">Website Preview</h4>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, website: '' }));
                              setLinkPreview(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>

                        <div className="flex gap-2 items-start">
                          {linkPreview.favicon && (
                            <img
                              src={linkPreview.favicon}
                              alt="Favicon"
                              className="w-4 h-4 rounded flex-shrink-0 mt-0.5"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm">
                              {linkPreview.title || formData.website}
                            </p>
                            {linkPreview.description && (
                              <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                                {linkPreview.description}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 truncate mt-1">{formData.website}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                Add
              </Button>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
              Event Image
            </label>
            <input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            {imagePreview && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Preview:</p>
                <div className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Event preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, image: null }));
                      setImagePreview(existingImageUrl || null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 pt-6">
            <Button type="button" variant="outline" onClick={() => navigate(`/events/${id}`)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !!timeError}
              className="min-w-[120px]"
            >
              {submitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>Updating...</span>
                </div>
              ) : (
                'Update Event'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
